import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';
import BurnerWidget from './components/common/BurnerWidget';
import { useAuth } from './hooks/useAuth';
import { pollingService } from './services/pollingService';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AuctionDetail = lazy(() => import('./pages/AuctionDetail'));
const CreateAuction = lazy(() => import('./pages/CreateAuction'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Payments = lazy(() => import('./pages/Payments'));
const EditAuction = lazy(() => import('./pages/EditAuction'));
const Auctions = lazy(() => import('./pages/Auctions'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Approvals = lazy(() => import('./pages/Approvals'));

/**
 * Sends a signed-in user to wherever their role actually lands, instead of
 * assuming /dashboard. A committee member has no dashboard -- routing them
 * there produced a redirect loop back to this component.
 */
const RoleLanding = () => {
  const { isAuthenticated, landingPath, loading } = useAuth();
  if (loading) return <LoadingSpinner fullScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={landingPath} replace />;
};

function App() {
  const { loading, isAuthenticated, user } = useAuth();

  // Live updates. Polling, not sockets -- see services/pollingService.js.
  useEffect(() => {
    if (isAuthenticated && user) {
      pollingService.connect(user.id);
    } else {
      pollingService.disconnect();
    }
    return () => pollingService.disconnect();
  }, [isAuthenticated, user]);

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar />

        {/* px-3 on phones, px-4 from sm up; py trimmed on small screens
            so the first card isn't pushed below the fold. */}
        <main className="flex-grow w-full container mx-auto px-3 sm:px-4 py-4 sm:py-8">
          <AnimatePresence mode="wait">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* ---------- Public ---------- */}
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />

                {/* Sends each role to its own landing page */}
                <Route path="/home" element={<RoleLanding />} />

                {/* ---------- Signed in, permission gated ---------- */}
                <Route element={<ProtectedRoute requires="page.auctions" />}>
                  <Route path="/auctions" element={<Auctions />} />
                </Route>

                <Route element={<ProtectedRoute requires="page.auction_detail" />}>
                  <Route path="/auctions/:id" element={<AuctionDetail />} />
                </Route>

                <Route element={<ProtectedRoute requires="page.dashboard" />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                </Route>

                <Route element={<ProtectedRoute requires="page.payments" />}>
                  <Route path="/payments" element={<Payments />} />
                </Route>

                <Route element={<ProtectedRoute requires="page.notifications" />}>
                  <Route path="/notifications" element={<Notifications />} />
                </Route>

                {/* ---------- Auction creator ---------- */}
                <Route element={<ProtectedRoute requires="page.create_auction" />}>
                  <Route path="/auctions/create" element={<CreateAuction />} />
                </Route>

                <Route element={<ProtectedRoute requires="page.edit_auction" />}>
                  <Route path="/auctions/edit/:id" element={<EditAuction />} />
                </Route>

                {/* ---------- Auction approver ---------- */}
                <Route element={<ProtectedRoute requires="page.approvals" />}>
                  <Route path="/approvals" element={<Approvals />} />
                </Route>

                {/* ---------- Admin dashboard ----------
                    Reachable by ANY of its tab permissions, not page.admin
                    alone. The dashboard shows only the tabs the role holds,
                    so a committee member granted report.view lands on
                    Reports and sees nothing else. Gating the route on
                    page.admin meant an action permission like report.view
                    had no page to be exercised on and appeared to do
                    nothing. */}
                <Route
                  element={
                    <ProtectedRoute
                      requiresAny={[
                        'page.admin',
                        'user.view',
                        'payment.view_all',
                        'payment.verify',
                        'payment.refund',
                        'auction.approve',
                        'announcement.manage',
                        'report.view',
                        'audit.view',
                        'page.roles',
                      ]}
                    />
                  }
                >
                  <Route path="/admin" element={<AdminDashboard />} />
                </Route>

                {/* Unknown path -> role landing, not a blind bounce home */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </AnimatePresence>
        </main>

        <Footer />
        <BurnerWidget />
      </div>
    </ErrorBoundary>
  );
}

export default App;
